<template>
  <div class="p-4 min-h-screen bg-gray-100">
    <div class="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mx-auto page-card">
    <div class="flex items-center justify-between mb-3">
      <h1 class="text-xl font-semibold">Outreach Emails</h1>
      <!-- outreach_supervisor only: read everyone's outreach. Editing and sending
           stay limited to mailboxes you can send from. -->
      <label
        v-if="canUseSupervisorMode"
        class="inline-flex items-center gap-2 text-sm cursor-pointer select-none"
        title="Show every analyst's drafts and threads. You can still only edit and send from your own mailboxes."
      >
        <input v-model="outreachSupervisorMode" type="checkbox" class="rounded border-gray-300 text-amber-600 focus:ring-amber-500" />
        <span :class="outreachSupervisorMode ? 'text-amber-700 font-medium' : 'text-gray-600'">Supervisor Mode</span>
      </label>
    </div>
    <div
      v-if="canUseSupervisorMode && outreachSupervisorMode"
      class="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
    >
      Supervisor Mode: showing every analyst's outreach. Anything from a mailbox you can't send from opens read-only.
    </div>

    <!-- Status tabs -->
    <div class="border-b border-gray-200 mb-4 flex gap-1">
      <button
        v-for="tab in visibleTabs"
        :key="tab.key"
        type="button"
        @click="selectTab(tab.key)"
        :class="activeTab === tab.key
          ? 'border-indigo-500 text-indigo-600'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
        class="px-4 py-2 -mb-px border-b-2 text-sm font-medium inline-flex items-center gap-1.5"
      >{{ tab.label }}<span
          v-if="tab.key === 'failed' && failedCount > 0"
          class="relative group inline-flex"
        >
          <ExclamationCircleIcon class="w-4 h-4 text-red-500" />
          <span class="pointer-events-none absolute left-1/2 -translate-x-1/2 top-7 z-30 hidden group-hover:block w-48 p-2 rounded-md bg-gray-800 text-white text-[11px] font-normal normal-case tracking-normal leading-relaxed shadow-lg text-center">
            {{ failedCount }} failed send{{ failedCount === 1 ? '' : 's' }} — need attention
          </span>
        </span><span
          v-if="tab.key === 'scheduled' && overdueScheduledCount > 0"
          class="relative group inline-flex"
        >
          <ExclamationTriangleIcon class="w-4 h-4 text-amber-500" />
          <span class="pointer-events-none absolute left-1/2 -translate-x-1/2 top-7 z-30 hidden group-hover:block w-56 p-2 rounded-md bg-gray-800 text-white text-[11px] font-normal normal-case tracking-normal leading-relaxed shadow-lg text-center">
            {{ overdueScheduledCount }} scheduled send{{ overdueScheduledCount === 1 ? '' : 's' }} overdue by 30+ min — the send job may not be running
          </span>
        </span></button>
    </div>

    <!-- Filters -->
    <div v-if="activeTab !== 'dashboard'" class="border border-gray-200 rounded-md bg-gray-50 p-3 mb-4">
      <div class="flex items-center justify-between mb-2">
        <button
          type="button"
          @click="filtersExpanded = !filtersExpanded"
          class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-700"
        >
          <ChevronDownIcon v-if="filtersExpanded" class="w-4 h-4 shrink-0" />
          <ChevronRightIcon v-else class="w-4 h-4 shrink-0" />
          <span>Filters</span>
          <!-- Collapsed, the highlighted labels that say a filter is set are hidden
               with them -- so the count comes up here instead. -->
          <span
            v-if="!filtersExpanded && activeFilterCount"
            class="px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-800 text-[10px] normal-case tracking-normal"
          >{{ activeFilterCount }} active</span>
        </button>
        <div class="flex items-center gap-3">
          <button
            v-if="hasActiveFilters"
            type="button"
            @click="clearFilters"
            class="text-xs text-indigo-600 hover:text-indigo-800"
          >Clear all</button>
          <button
            type="button"
            @click="refresh"
            :disabled="loading"
            class="text-xs px-2 py-1 border border-gray-300 rounded-md bg-white hover:bg-gray-100 disabled:opacity-40"
          >{{ loading ? 'Refreshing…' : 'Refresh' }}</button>
        </div>
      </div>
      <div v-show="filtersExpanded">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div class="flex flex-col gap-1">
          <FilterFieldLabel label="Company" :active="filterIsActive('companyName')" @clear="clearFilter('companyName')" />
          <input
            type="text"
            v-model="filters.companyName.value"
            @input="onCompanyNameInput"
            @keyup.enter="onCompanyNameEnter"
            placeholder="Search company names…"
            title="Matches part of a company's name or DBA. Separate alternatives with |"
            class="border rounded-md p-1 text-xs h-8 bg-white w-full"
          />
        </div>
        <div class="flex flex-col gap-1">
          <FilterFieldLabel label="Client" :active="filterIsActive('client')" :count="filterCount('client')" @clear="clearFilter('client')" />
          <div class="filter-typeahead w-full">
            <TypeAheadMultiSelectBox
              :options="filters.client.options"
              :default-value="filters.client.value"
              select-multiple
              placeholder="Search clients…"
              no-selection-message="All clients"
              @update-value="onFilterChange('client', $event)"
            />
          </div>
        </div>
        <div class="flex flex-col gap-1">
          <FilterFieldLabel label="Campaign" :active="filterIsActive('campaign')" :count="filterCount('campaign')" @clear="clearFilter('campaign')" />
          <div class="filter-typeahead w-full">
            <TypeAheadMultiSelectBox
              :options="filters.campaign.options"
              :default-value="filters.campaign.value"
              select-multiple
              placeholder="Search campaigns…"
              no-selection-message="All active campaigns"
              @update-value="onFilterChange('campaign', $event)"
            />
          </div>
        </div>
        <div class="flex flex-col gap-1">
          <FilterFieldLabel label="Email Template" :active="filterIsActive('emailTemplate')" :count="filterCount('emailTemplate')" @clear="clearFilter('emailTemplate')" />
          <div class="filter-typeahead w-full">
            <TypeAheadMultiSelectBox
              :options="filters.emailTemplate.options"
              :default-value="filters.emailTemplate.value"
              select-multiple
              placeholder="Search templates…"
              no-selection-message="All templates"
              @update-value="onFilterChange('emailTemplate', $event)"
            />
          </div>
        </div>
        <div class="flex flex-col gap-1">
          <FilterFieldLabel label="Next action" :active="filterIsActive('nextAction')" :count="filterCount('nextAction')" @clear="clearFilter('nextAction')" />
          <div class="filter-typeahead w-full">
            <TypeAheadMultiSelectBox
              :options="nextActionOptions"
              :default-value="filters.nextAction.value"
              select-multiple
              placeholder="Search next actions…"
              no-selection-message="All next actions"
              @update-value="onFilterChange('nextAction', $event)"
            />
          </div>
        </div>
        <div class="flex flex-col gap-1">
          <FilterFieldLabel label="Next action due" :active="filterIsActive('nextActionDueAfter', 'nextActionDueBefore')" @clear="clearFilter('nextActionDueAfter', 'nextActionDueBefore')" />
          <div class="flex items-center gap-1">
            <input
              type="date"
              v-model="filters.nextActionDueAfter.value"
              @change="onDateFilterChange"
              title="Due on or after"
              class="border rounded-md p-1 text-xs h-8 bg-white w-full"
            />
            <span class="text-gray-400 text-xs shrink-0">–</span>
            <input
              type="date"
              v-model="filters.nextActionDueBefore.value"
              @change="onDateFilterChange"
              title="Due on or before"
              class="border rounded-md p-1 text-xs h-8 bg-white w-full"
            />
          </div>
        </div>
        <div class="flex flex-col gap-1">
          <FilterFieldLabel label="CRM detail" :active="filterIsActive('crmDetail')" :count="filterCount('crmDetail')" @clear="clearFilter('crmDetail')" />
          <div class="filter-typeahead w-full">
            <TypeAheadMultiSelectBox
              :options="filters.crmDetail.options"
              :default-value="filters.crmDetail.value"
              select-multiple
              placeholder="Search CRM details…"
              no-selection-message="All CRM details"
              @update-value="onFilterChange('crmDetail', $event)"
            />
          </div>
        </div>
        <div v-if="hasMultipleFromAddresses" class="flex flex-col gap-1">
          <FilterFieldLabel label="From mailbox" :active="filterIsActive('fromEmail')" :count="filterCount('fromEmail')" @clear="clearFilter('fromEmail')" />
          <div class="filter-typeahead w-full">
            <TypeAheadMultiSelectBox
              :options="filters.fromEmail.options"
              :default-value="filters.fromEmail.value"
              select-multiple
              placeholder="Search mailboxes…"
              no-selection-message="All my mailboxes"
              @update-value="onFilterChange('fromEmail', $event)"
            />
          </div>
        </div>
        <div v-if="activeTab === 'threads'" class="flex flex-col gap-1">
          <FilterFieldLabel label="Reply status" :active="filterIsActive('replyState')" :count="filterCount('replyState')" @clear="clearFilter('replyState')" />
          <div class="filter-typeahead w-full">
            <TypeAheadMultiSelectBox
              :options="filters.replyState.options"
              :default-value="filters.replyState.value"
              select-multiple
              placeholder="Search reply statuses…"
              no-selection-message="All reply statuses"
              @update-value="onFilterChange('replyState', $event)"
            />
          </div>
        </div>
      </div>

      <!-- Toggles sit on their own row rather than in the grid above: they have no
           label of their own, so in a labelled-field cell they read as an orphaned
           control and wrap raggedly against the dropdowns. -->
      <div class="flex flex-wrap items-center gap-x-6 gap-y-2 mt-3">
        <label
          class="inline-flex items-center gap-2 text-xs font-medium cursor-pointer px-1.5 py-0.5 -ml-1.5 rounded"
          :class="filterIsActive('mineOnly') ? 'bg-indigo-100 text-indigo-800' : 'text-gray-600'"
        >
          <input
            type="checkbox"
            v-model="filters.mineOnly.value"
            @change="onDateFilterChange"
          />
          {{ activeTab === 'threads' ? 'Only threads I started' : 'Only drafts I created' }}
          <span class="relative group flex items-center">
            <InformationCircleIcon class="w-4 h-4 text-gray-400 cursor-help" />
            <span class="pointer-events-none absolute left-0 top-6 z-30 hidden group-hover:block w-64 p-3 rounded-md bg-gray-800 text-white text-[11px] font-normal normal-case tracking-normal leading-relaxed shadow-lg text-left">
              This queue holds every draft from a mailbox you can send from, including
              ones your colleagues prepared. Tick this to see only your own.
            </span>
          </span>
        </label>
        <label
          class="inline-flex items-center gap-2 text-xs font-medium cursor-pointer px-1.5 py-0.5 rounded"
          :class="filterIsActive('excludeHasReplies') ? 'bg-indigo-100 text-indigo-800' : 'text-gray-600'"
        >
          <input
            type="checkbox"
            v-model="filters.excludeHasReplies.value"
            @change="onDateFilterChange"
          />
          {{ activeTab === 'threads' ? 'Exclude threads with follow-ups' : 'Exclude drafts with follow-ups' }}
        </label>
      </div>

      <!-- Status summary: counts across ALL tabs for the current filters,
           collapsed by default. The stale warning stays visible when collapsed. -->
      <div class="mt-3 border-t border-gray-200 pt-2">
        <button
          type="button"
          @click="summaryExpanded = !summaryExpanded"
          class="w-full flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-700"
        >
          <ChevronDownIcon v-if="summaryExpanded" class="w-4 h-4 shrink-0" />
          <ChevronRightIcon v-else class="w-4 h-4 shrink-0" />
          <span>Status summary</span>
          <span v-if="summary" class="text-gray-400 normal-case font-normal tracking-normal">
            ({{ summary.total }} across all tabs)
          </span>
          <span
            v-if="needsAttentionCount > 0"
            class="relative group ml-auto inline-flex items-center gap-1 text-amber-700 bg-amber-100 rounded-full px-2 py-0.5 normal-case tracking-normal font-medium cursor-help"
          >
            <ExclamationTriangleIcon class="w-3.5 h-3.5" />
            {{ needsAttentionCount }} need attention
            <span class="pointer-events-none absolute right-0 top-7 z-30 hidden group-hover:block w-64 p-3 rounded-md bg-gray-800 text-white text-xs font-normal normal-case tracking-normal leading-relaxed shadow-lg text-left">
              <span class="flex justify-between gap-4" v-for="row in needsAttentionBreakdown" :key="row.label">
                <span>{{ row.label }}</span>
                <span class="tabular-nums font-semibold">{{ row.count }}</span>
              </span>
              <span class="mt-2 block text-gray-400">For the current filters.</span>
            </span>
          </span>
        </button>
        <div v-if="summaryExpanded" class="mt-2">
          <div v-if="summaryLoading" class="text-xs text-gray-400">Loading…</div>
          <div v-else-if="summary" class="flex flex-wrap gap-2">
            <span
              v-for="row in summaryRows"
              :key="row.value"
              :class="[statusClass(row.value), row.count === 0 ? 'opacity-40' : '']"
              class="px-2 py-0.5 rounded-full text-xs"
            >{{ row.label }}: {{ row.count }}</span>
          </div>
        </div>
      </div>
      </div>
    </div>

    <!-- Dashboard tab: Delegated to OutreachDashboard component -->
    <template v-if="activeTab === 'dashboard'">
      <OutreachDashboard :filters="filters" @update-filter="onDashboardUpdateFilter" />
    </template>

    <template v-else>

    <!-- Bulk actions. Drafts / Failed show it once something is selected; on
         Threads it's always there, disabled until a selection -- that's how
         threads get filed, so it shouldn't have to be discovered. -->
    <div
      v-if="selectable && (selectedDraftIds.length || activeTab === 'threads')"
      class="flex items-center gap-3 mb-3 px-3 py-2 rounded-md border text-sm"
      :class="selectedDraftIds.length ? 'border-indigo-200 bg-indigo-50' : 'border-gray-200 bg-gray-50'"
    >
      <span
        class="font-medium"
        :class="selectedDraftIds.length ? 'text-indigo-800' : 'text-gray-400'"
      >{{ selectedDraftIds.length ? `${selectedDraftIds.length} selected` : 'None selected' }}</span>
      <button
        v-if="activeTab === 'sent' || activeTab === 'threads'"
        type="button"
        @click="showFollowUpModal = true"
        :disabled="!selectedDraftIds.length"
        class="px-3 py-1 rounded-md text-sm bg-dark-primary text-white disabled:opacity-40"
      >Prepare follow-ups</button>
      <button
        v-else-if="activeTab === 'active' || activeTab === 'failed'"
        type="button"
        @click="onBulkDiscard"
        :disabled="discarding"
        class="px-3 py-1 rounded-md text-sm border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-40"
      >{{ discarding ? 'Discarding…' : 'Discard' }}</button>
      <!-- Filing is personal: it moves the threads for this user only. -->
      <template v-if="activeTab === 'threads'">
        <button
          v-if="threadBox !== 'archive'"
          type="button"
          @click="onPinThreads(selectedDraftIds, !selectionAllPinned)"
          :disabled="moving || !selectedDraftIds.length"
          class="px-3 py-1 rounded-md text-sm border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-white"
        >{{ selectionAllPinned ? 'Unpin' : 'Pin' }}</button>
        <button
          v-if="threadBox !== 'archive'"
          type="button"
          @click="onMoveThreads(selectedDraftIds, 'archive')"
          :disabled="moving || !selectedDraftIds.length"
          class="px-3 py-1 rounded-md text-sm border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-white"
        >Archive</button>
        <select
          :value="''"
          @change="onMoveSelect($event)"
          :disabled="moving || !selectedDraftIds.length"
          class="border border-gray-300 rounded-md text-sm py-1 pl-2 pr-7 bg-white disabled:opacity-40"
        >
          <option value="" disabled>Move to…</option>
          <option v-for="dest in moveDestinations" :key="dest.value" :value="dest.value">{{ dest.label }}</option>
        </select>
      </template>
      <button
        v-if="selectedDraftIds.length"
        type="button"
        @click="clearSelection"
        class="text-xs text-indigo-600 hover:text-indigo-800"
      >Clear</button>
    </div>

    <!-- Threads: the user's own boxes down the side, the conversations beside them. -->
    <div v-if="activeTab === 'threads'" class="flex gap-4 items-start">
    <ThreadFolderSidebar
      :box="threadBox"
      :folders="folders"
      :counts="boxCounts"
      @select="selectBox"
      @create="onCreateFolder"
      @rename="onRenameFolder"
      @remove="onDeleteFolder"
    />
    <div class="flex-1 min-w-0">

    <div v-if="loading" class="text-gray-500 py-8 text-center">Loading…</div>

    <div v-else-if="threads.length === 0" class="text-gray-500 py-8 text-center">
      {{ emptyMessage }}
    </div>

    <!-- Replies is a different row shape: one conversation, not one draft. The
         same prospect answering three sends is one thing to deal with. -->
    <!-- Compact, like the Sent tab was: eight columns don't fit at text-sm.
         table-fixed so it fits beside the folder sidebar: the narrow columns
         have set widths and Company / Subject share what's left, truncating,
         rather than the table growing past the card to fit its content. -->
    <table v-else class="w-full border-collapse text-xs table-fixed">
      <thead>
        <tr class="text-left border-b border-gray-300 text-gray-600">
          <th class="py-2 pr-3 w-8">
            <input type="checkbox" :checked="allSelected" @change="toggleSelectAll" title="Select all" />
          </th>
          <th class="py-2 pr-3">Company</th>
          <th class="py-2 pr-3 w-32">Last message</th>
          <th class="py-2 pr-3">Subject</th>
          <th class="py-2 pr-3 w-24">CRM status</th>
          <th class="py-2 pr-3 w-32">Next action</th>
          <th class="py-2 pr-3 w-36">Mailbox</th>
          <th class="py-2 pr-3 w-24">Replies</th>
          <th class="py-2 pr-3 w-28">Last activity</th>
          <th class="py-2 w-12"></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="thread in threads"
          :key="thread.id"
          @click="openThread(thread)"
          class="border-b border-gray-200 cursor-pointer"
          :class="thread.pinned ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-gray-50'"
        >
          <td class="py-2 pr-3" @click.stop>
            <input type="checkbox" :checked="isSelected(thread.id)" @change="toggleSelect(thread.id)" />
          </td>
          <td class="py-2 pr-3">
            <div class="font-medium text-gray-800 truncate" :title="(thread.company || {}).company_name">{{ (thread.company || {}).company_name || '—' }}</div>
            <div class="text-gray-400 truncate" :title="(thread.client || {}).client_name">{{ (thread.client || {}).client_name || '' }}</div>
            <!-- Under All, say where each one is kept; in a box it's the box. -->
            <div v-if="threadBox === 'all' && (thread.folder || thread.archived)" class="mt-0.5">
              <span
                class="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px]"
              >{{ thread.folder ? thread.folder.name : 'Archived' }}</span>
            </div>
          </td>
          <td class="py-2 pr-3">
            <div class="text-gray-800 truncate" :title="thread.latest_from">{{ thread.latest_name || thread.latest_from || '—' }}</div>
            <!-- Whose move it is (reply_state, worked out by the server). Amber
                 because an unanswered reply is the one state on this page that
                 is work. A reply can be ruled as needing no answer, or as a
                 do-not-contact, from the label itself. -->
            <div @click.stop class="relative inline-block">
              <button
                type="button"
                @click="toggleReplyMenu(thread.id)"
                :disabled="moving || !canRuleOnReply(thread)"
                :title="replyStateTitle(thread)"
                class="disabled:cursor-default"
                :class="replyStateClass(thread.reply_state)"
              >{{ replyStateLabel(thread.reply_state) }}<ChevronDownIcon
                v-if="canRuleOnReply(thread)" class="inline w-3 h-3 ml-0.5 -mt-px" /></button>
              <div
                v-if="replyMenuThreadId === thread.id"
                class="absolute z-10 left-0 mt-1 w-40 rounded-md border border-gray-200 bg-white shadow-md py-1"
              >
                <button
                  v-for="option in replyStatusOptions(thread)"
                  :key="option.value || 'clear'"
                  type="button"
                  @click="onSetReplyStatus([thread.id], option.value)"
                  class="block w-full text-left px-3 py-1 text-gray-700 hover:bg-gray-100"
                >{{ option.label }}</button>
              </div>
            </div>
          </td>
          <td class="py-2 pr-3">
            <div class="truncate text-gray-700" :title="thread.latest_subject">{{ thread.latest_subject || '—' }}</div>
          </td>
          <!-- Where the company stands now, in this thread's campaign. -->
          <td class="py-2 pr-3 text-gray-700 truncate" :title="thread.crm_status">{{ thread.crm_status || '—' }}</td>
          <td class="py-2 pr-3">
            <div class="truncate text-gray-700" :title="thread.crm_next_action">{{ thread.crm_next_action || '—' }}</div>
            <div v-if="thread.crm_next_action_due_date" class="text-gray-400">
              Due {{ formatDueDate(thread.crm_next_action_due_date) }}
            </div>
          </td>
          <td class="py-2 pr-3 text-gray-500 truncate" :title="thread.mailbox">{{ thread.mailbox || '—' }}</td>
          <td class="py-2 pr-3">
            <span
              v-if="thread.reply_count"
              class="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs"
            >{{ thread.reply_count }}</span>
            <span v-else class="text-gray-300 text-xs">—</span>
            <!-- A bounce on the same thread is worth seeing next to a reply:
                 one address failed even though someone else answered. -->
            <span
              v-if="thread.has_bounce"
              class="ml-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs"
              title="A message on this thread bounced"
            >bounced</span>
          </td>
          <td class="py-2 pr-3 whitespace-nowrap text-gray-600">{{ formatDate(thread.latest_activity_at) }}</td>
          <!-- Pin, and one-click archive (or back to the Inbox from the
               Archive). No pin on an archived row: archiving clears it. -->
          <td class="py-2 text-right whitespace-nowrap" @click.stop>
            <button
              v-if="!thread.archived"
              type="button"
              @click="onPinThreads([thread.id], !thread.pinned)"
              :disabled="moving"
              :title="thread.pinned ? 'Unpin' : 'Pin to top'"
              class="mr-1.5 disabled:opacity-40"
              :class="thread.pinned ? 'text-amber-600 hover:text-amber-800' : 'text-gray-400 hover:text-gray-700'"
            ><PinIcon :filled="thread.pinned" class="w-4 h-4" /></button>
            <button
              v-if="thread.archived"
              type="button"
              @click="onMoveThreads([thread.id], 'inbox')"
              :disabled="moving"
              title="Move to Inbox"
              class="text-gray-400 hover:text-gray-700 disabled:opacity-40"
            ><InboxIcon class="w-4 h-4" /></button>
            <button
              v-else
              type="button"
              @click="onMoveThreads([thread.id], 'archive')"
              :disabled="moving"
              title="Archive"
              class="text-gray-400 hover:text-gray-700 disabled:opacity-40"
            ><ArchiveBoxIcon class="w-4 h-4" /></button>
          </td>
        </tr>
      </tbody>
    </table>
    </div>
    </div>

    <template v-else>
    <div v-if="loading" class="text-gray-500 py-8 text-center">Loading…</div>

    <div v-else-if="drafts.length === 0" class="text-gray-500 py-8 text-center">
      {{ emptyMessage }}
    </div>

    <table v-else class="w-full border-collapse" :class="activeTab === 'sent' ? 'text-xs is-compact' : 'text-sm'">
      <thead>
        <tr class="text-left border-b border-gray-300 text-gray-600">
          <th v-if="selectable" class="py-2 pr-3 w-8">
            <input type="checkbox" :checked="allSelected" @change="toggleSelectAll" title="Select all" />
          </th>
          <th class="py-2 pr-4" :class="activeTab === 'sent' ? 'w-44' : 'w-56'">Company</th>
          <th class="py-2 pr-4">Subject</th>
          <th class="py-2 pr-4">Recipient</th>
          <th v-if="hasMultipleFromAddresses" class="py-2 pr-4">From</th>
          <th class="py-2 pr-4">CRM detail</th>
          <th v-if="activeTab === 'sent'" class="py-2 pr-4">Next action</th>
          <th v-if="activeTab === 'sent'" class="py-2 pr-4">Due</th>
          <th class="py-2 pr-4">{{ activeTab === 'sent' ? 'Sent' : activeTab === 'scheduled' ? 'Sends' : 'Created' }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="draft in drafts"
          :key="draft.id"
          class="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
          @click="openDraft(draft)"
        >
          <td v-if="selectable" class="py-2 pr-3" @click.stop>
            <input type="checkbox" :checked="isSelected(draft.id)" @change="toggleSelect(draft.id)" />
          </td>
          <td class="py-2 pr-4" :class="activeTab === 'sent' ? 'w-44' : 'w-56'">
            <span class="flex items-center gap-1.5">
              <span
                v-if="isStale(draft)"
                :title="`Unsent for ${ageInDays(draft.created_at)} days — needs attention`"
                class="inline-flex shrink-0"
              >
                <ExclamationTriangleIcon class="w-4 h-4 text-amber-500" />
              </span>
              <span
                v-if="isOverdueScheduled(draft)"
                :title="`Scheduled send overdue — was due ${formatDate(draft.scheduled_send_at)}; the send job may not be running`"
                class="inline-flex shrink-0"
              >
                <ExclamationTriangleIcon class="w-4 h-4 text-amber-500" />
              </span>
              <span class="flex-1 min-w-0 break-words">{{ draft.company_name || '—' }}</span>
              <span
                v-if="draft.status === 'failed'"
                class="relative group ml-1 shrink-0 inline-flex text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 whitespace-nowrap cursor-help"
              >failed
                <span class="pointer-events-none absolute left-0 top-6 z-30 hidden group-hover:block w-72 p-3 rounded-md bg-gray-800 text-white text-[11px] font-normal normal-case tracking-normal leading-relaxed shadow-lg text-left whitespace-normal break-words">
                  {{ draft.send_error || 'Send failed' }}
                </span>
              </span>
              <span
                v-if="draft.in_reply_to"
                title="Follow-up reply"
                class="ml-1 shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 whitespace-nowrap"
              >↩ follow-up</span>
              <span
                v-if="isSomeoneElses(draft)"
                :title="`Created by ${draft.owner.name}`"
                class="ml-1 shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-purple-100 text-purple-800 text-[10px] font-semibold"
              >{{ ownerInitials(draft.owner) }}</span>
              <span
                v-if="activeTab === 'sent' && draft.sent_reply_count > 0"
                :title="`${draft.sent_reply_count} follow-up${draft.sent_reply_count === 1 ? '' : 's'} sent in reply`"
                class="ml-1 shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-800 whitespace-nowrap"
              >↳ {{ draft.sent_reply_count }} repl{{ draft.sent_reply_count === 1 ? 'y' : 'ies' }}</span>
              <span
                v-if="SHOW_INBOUND_RESPONSES && activeTab === 'sent' && draft.response_count > 0"
                :title="`${draft.response_count} inbound repl${draft.response_count === 1 ? 'y' : 'ies'} received`"
                class="ml-1 shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-800 whitespace-nowrap"
              >✉ {{ draft.response_count }} in</span>
            </span>
          </td>
          <td class="py-2 pr-4 truncate" :class="activeTab === 'sent' ? 'max-w-[10rem]' : 'max-w-xs'">{{ draft.subject || '—' }}</td>
          <td
            class="py-2 pr-4 truncate"
            :class="activeTab === 'sent' ? 'max-w-[10rem]' : 'max-w-[14rem]'"
            :title="draft.recipient_email || ''"
          >{{ draft.recipient_email || '—' }}</td>
          <td v-if="hasMultipleFromAddresses" class="py-2 pr-4 truncate max-w-[12rem]">{{ draft.from_email || '—' }}</td>
          <td class="py-2 pr-4">{{ draft.crm_detail ? draft.crm_detail.action_name : '—' }}</td>
          <td v-if="activeTab === 'sent'" class="py-2 pr-4">{{ draft.company_crm_next_action || '—' }}</td>
          <td v-if="activeTab === 'sent'" class="py-2 pr-4 whitespace-nowrap">{{ formatDueDate(draft.company_crm_next_action_due_date) }}</td>
          <td class="py-2 pr-4" :class="(activeTab === 'active' && isStale(draft)) || (activeTab === 'scheduled' && isOverdueScheduled(draft)) ? 'text-amber-600 font-medium' : ''">
            <template v-if="activeTab === 'sent'">{{ formatDate(draft.sent_at) }}</template>
            <template v-else-if="activeTab === 'scheduled'">{{ formatDate(draft.scheduled_send_at) }}</template>
            <template v-else>
              {{ formatDate(draft.created_at) }}
              <span v-if="isStale(draft)" class="text-xs">· {{ ageInDays(draft.created_at) }}d</span>
            </template>
          </td>
        </tr>
      </tbody>
    </table>
    </template>

    <FollowUpTemplateModal
      :open="showFollowUpModal"
      bulk
      :count="selectedDraftIds.length"
      :submitting="followUpSubmitting"
      @confirm="onBulkFollowUp"
      @cancel="showFollowUpModal = false"
    />

    <div v-if="!loading && (prev || next)" class="flex items-center gap-2 mt-4 justify-end">
      <button :disabled="!prev" @click="loadList(page - 1)" class="px-3 py-1 border rounded-md text-sm disabled:opacity-40">Prev</button>
      <span class="text-sm text-gray-600">Page {{ page }} of {{ numPages || 1 }}</span>
      <button :disabled="!next" @click="loadList(page + 1)" class="px-3 py-1 border rounded-md text-sm disabled:opacity-40">Next</button>
    </div>

    </template>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import { getOutreachDrafts, getOutreachDraftStatusSummary, getOutreachDraftFromAddresses, bulkPrepareFollowUpOutreachDrafts, bulkDiscardOutreachDrafts } from '@/services/OutreachDraft'
import { getMinimalActiveCampaigns } from '@/services/Campaign'
import { getClients } from '@/services/Client'
import { getEmailTemplates } from '@/services/EmailTemplates'
import { getActions } from '@/services/CrmAction'
import TypeAheadMultiSelectBox from '@/components/base-components/TypeAheadMultiSelectBox.vue'
import FilterFieldLabel from '@/components/outreach-drafts/FilterFieldLabel.vue'
import FollowUpTemplateModal from '@/components/outreach-drafts/FollowUpTemplateModal.vue'
import { getOutreachThreads, bulkPrepareFollowUpThreads, moveOutreachThreads, getOutreachThreadBoxCounts, pinOutreachThreads, setOutreachThreadReplyStatus } from '@/services/OutreachThread'
import { getOutreachFolders, createOutreachFolder, updateOutreachFolder, deleteOutreachFolder } from '@/services/OutreachFolder'
import ThreadFolderSidebar from '@/components/outreach-drafts/ThreadFolderSidebar.vue'
import PinIcon from '@/components/outreach-drafts/PinIcon.vue'
import OutreachDashboard from '@/components/outreach-drafts/OutreachDashboard.vue'
import { CRM_NEXT_ACTION_OPTIONS } from '@/utils/crmNextActionOptions'
import { outreachSupervisorMode, isOutreachSupervisor } from '@/utils/outreachSupervisorMode'
import { ExclamationTriangleIcon, ExclamationCircleIcon, ChevronDownIcon, ChevronRightIcon, InformationCircleIcon, InboxIcon, ArchiveBoxIcon } from '@heroicons/vue/20/solid'

const store = useStore()
const route = useRoute()
const router = useRouter()
const $toast = useToast()

const drafts = ref([])
// The replies inbox: one row per conversation, loaded separately.
const threads = ref([])

// Which of the user's own boxes the Threads tab shows: 'inbox', 'archive',
// 'all', or a folder id as a string. Personal -- filing a thread moves it for
// this user only. In the URL (?box=, absent for the Inbox) like the tab, so
// opening a thread and coming Back returns to the same box.
function boxFromUrl() {
  const raw = String(route.query.box || '')
  return ['archive', 'all'].includes(raw) || /^\d+$/.test(raw) ? raw : 'inbox'
}
const threadBox = ref(boxFromUrl())
const folders = ref([])
const boxCounts = ref(null)   // {inbox, archive, folders: {id: n}}
const moving = ref(false)
const loading = ref(false)
const page = ref(1)
const next = ref(null)
const prev = ref(null)
const numPages = ref(1)

// Status summary across ALL tabs for the current filters. Collapsed by default.
const summary = ref(null) // { counts: {status: n}, total, stale_unsent }
const summaryExpanded = ref(false)
// Open by default -- the filters are how the page is normally driven; collapsing
// is for buying back the vertical space once they're set.
const filtersExpanded = ref(true)
const summaryLoading = ref(false)

// Status tabs. Each `key` maps to a backend status_group; every draft status
// rolls up into exactly one tab so nothing is hidden. "active" = workable
// pre-send (draft/in_progress/ready/blocked); "failed" is broken sends, split
// out so they don't hide among fresh drafts and can carry their own alert.
const tabs = [
  { key: 'active', label: 'Drafts' },
  { key: 'scheduled', label: 'Scheduled' },
  // Sent is hidden: Threads lists every sent email, a conversation per row, and
  // opening one there and from Sent led to the same page two different ways.
  // Its bulk "Prepare follow-ups" goes with it until Threads gets its own
  // bulk follow-up (pending design). The sent-tab code paths below are kept.
  { key: 'threads', label: 'Threads' },
  { key: 'failed', label: 'Failed' },
  { key: 'discarded', label: 'Discarded' },
  { key: 'dashboard', label: 'Dashboard' },
]

// Number of failed drafts under the current filters (spans all tabs via the
// summary). Drives the red indicator on the Failed tab.
const failedCount = computed(() => summary.value?.counts?.failed || 0)

// Scheduled drafts whose send time passed 30+ min ago without sending -- a sign
// the send job is stuck/stopped. Drives the amber indicator on the Scheduled tab.
const overdueScheduledCount = computed(() => summary.value?.overdue_scheduled || 0)

// The "need attention" badge rolls up every draft that needs analyst action:
// stale unsent drafts (>7d), failed sends, and overdue scheduled sends. These
// three status sets are disjoint, so the counts sum without double-counting.
const needsAttentionCount = computed(() => {
  if (!summary.value) return 0
  return (summary.value.stale_unsent || 0) + failedCount.value + overdueScheduledCount.value
})

// Per-category breakdown behind the "need attention" badge, shown on hover.
// Only non-zero rows appear; the counts sum to needsAttentionCount.
const needsAttentionBreakdown = computed(() => {
  const rows = []
  if (failedCount.value) rows.push({ count: failedCount.value, label: 'Failed sends' })
  const stale = summary.value?.stale_unsent || 0
  if (stale) rows.push({ count: stale, label: 'Unsent over 7 days' })
  if (overdueScheduledCount.value) rows.push({ count: overdueScheduledCount.value, label: 'Overdue scheduled sends' })
  return rows
})

// Filter tabs to hide dashboard from non-dev users
const visibleTabs = computed(() => {
  const userIsDev = store.state.auth.userPermissions?.isDev || false
  return userIsDev ? tabs : tabs.filter(t => t.key !== 'dashboard')
})

// The active tab lives in the URL (?tab=) so opening a draft and coming back
// lands on the tab you left from, and a reload/bookmark keeps it too.
const getInitialTab = () => {
  const tabFromUrl = route.query.tab
  const userIsDev = store.state.auth.userPermissions?.isDev || false
  // Prevent non-dev users from accessing dashboard tab
  if (tabFromUrl === 'dashboard' && !userIsDev) {
    router.push({ name: 'forbidden-page' })
    return 'active'
  }
  // Old links and Back from a preview can still say ?tab=sent.
  if (tabFromUrl === 'sent') return 'threads'
  return tabs.some(t => t.key === tabFromUrl) ? tabFromUrl : 'active'
}
const activeTab = ref(getInitialTab())

// Inbound-response surfacing is hidden for now (the "✉ N in" badge on the Sent
// tab). Flip to true to bring it back; the backend still records responses.
// The Sent tab's reply badge. On now that response_count counts real
// replies -- it previously included bounces and our own Outlook sends,
// which is why a badge reading "N inbound replies" stayed hidden.
const SHOW_INBOUND_RESPONSES = true

// Bulk selection: follow-ups on the Sent tab, discard on the Drafts tab. Holds
// selected draft ids across pages.
const selectedDraftIds = ref([])
const showFollowUpModal = ref(false)
const followUpSubmitting = ref(false)
const discarding = ref(false)

// Tabs that support row selection + a bulk action.
const selectable = computed(() =>
  activeTab.value === 'sent' || activeTab.value === 'active' || activeTab.value === 'failed'
  || activeTab.value === 'threads'
)

// The rows on screen: threads on the Threads tab, drafts everywhere else. The
// selection holds thread ids on Threads and draft ids elsewhere; switching tab
// clears it, so the two never mix.
const currentRows = computed(() => (activeTab.value === 'threads' ? threads.value : drafts.value))

const allSelected = computed(() =>
  currentRows.value.length > 0 && currentRows.value.every(d => selectedDraftIds.value.includes(d.id))
)

function isSelected(id) {
  return selectedDraftIds.value.includes(id)
}

function toggleSelect(id) {
  const i = selectedDraftIds.value.indexOf(id)
  if (i === -1) selectedDraftIds.value.push(id)
  else selectedDraftIds.value.splice(i, 1)
}

// Select-all toggles just the current page; selection persists across pages.
function toggleSelectAll() {
  const pageIds = currentRows.value.map(d => d.id)
  if (allSelected.value) {
    const drop = new Set(pageIds)
    selectedDraftIds.value = selectedDraftIds.value.filter(id => !drop.has(id))
  } else {
    selectedDraftIds.value = Array.from(new Set([...selectedDraftIds.value, ...pageIds]))
  }
}

function clearSelection() {
  selectedDraftIds.value = []
}

async function onBulkDiscard() {
  const n = selectedDraftIds.value.length
  if (!n) return
  if (!window.confirm(`Discard ${n} draft${n === 1 ? '' : 's'}? They'll move to the Discarded tab.`)) return
  discarding.value = true
  try {
    const res = await bulkDiscardOutreachDrafts(selectedDraftIds.value, store.state.auth.token)
    const done = res.discarded || 0
    const skipped = res.skipped || 0
    $toast.success(`Discarded ${done} draft${done === 1 ? '' : 's'}${skipped ? `, skipped ${skipped}` : ''}`)
    clearSelection()
    loadDrafts(page.value)
    loadSummary()
  } catch (e) {
    $toast.error(e?.response?.data?.detail || 'Failed to discard drafts')
  } finally {
    discarding.value = false
  }
}

// Why a thread was left out of a bulk follow-up, as the backend reports it.
const SKIP_REASONS = {
  bounced: 'bounced (fix and resend instead)',
  has_draft: 'already had a draft waiting',
  no_message_id: "can't be threaded (sent before threading)",
  no_message: 'had nothing sent yet',
  not_on_message: "latest email didn't reach any of your mailboxes",
}

async function onBulkFollowUp(templateId) {
  followUpSubmitting.value = true
  try {
    const token = store.state.auth.token
    const onThreads = activeTab.value === 'threads'
    const res = onThreads
      ? await bulkPrepareFollowUpThreads(selectedDraftIds.value, token, templateId)
      : await bulkPrepareFollowUpOutreachDrafts(selectedDraftIds.value, token, templateId)
    const created = res.created || 0
    const skippedRows = res.skipped || []
    const skipped = skippedRows.length
    $toast.success(`Created ${created} follow-up${created === 1 ? '' : 's'}${skipped ? `, skipped ${skipped}` : ''}`)
    // Threads report why each was skipped; say so rather than leave a bare count.
    if (onThreads && skipped) {
      const why = SKIP_REASONS
      const counts = {}
      skippedRows.forEach((r) => { counts[r.reason] = (counts[r.reason] || 0) + 1 })
      $toast.info(Object.entries(counts).map(([k, n]) => `${n} ${why[k] || k}`).join(' · '), { duration: 8000 })
    }
    showFollowUpModal.value = false
    clearSelection()
    loadList(page.value)
  } catch (e) {
    $toast.error(e?.response?.data?.detail || 'Failed to create follow-ups')
  } finally {
    followUpSubmitting.value = false
  }
}

// Whose move it is on a thread, as the server works it out
// (services/outreach_reply_state_service.py in the backend). Drives the label
// under each thread's last message and the Reply status filter's options.
const REPLY_STATES = {
  needs_reply: { label: 'Needs reply', cls: 'text-amber-600 font-medium' },
  awaiting_reply: { label: 'Awaiting reply', cls: 'text-gray-400' },
  not_delivered: { label: 'Not delivered', cls: 'text-red-600 font-medium' },
  auto_reply: { label: 'Auto-reply', cls: 'text-gray-400' },
  no_reply_needed: { label: 'No reply needed', cls: 'text-gray-400' },
  do_not_contact: { label: 'Do not contact', cls: 'text-red-600' },
}

// All draft-queue filters in one object. `param` is the query-string key sent to
// the OutreachDraftFilter backend; multi-select `value`s are arrays of ids.
// `param` is the query-string key sent to the backend. `type` is how the value
// survives a round trip through the page URL: 'ids' parses back to Numbers,
// because the multi-select compares option values by identity and a string "12"
// would never match the option whose value is 12.
const filters = reactive({
  // Free text, matched server-side against the company's name and DBA ("|"
  // separates alternatives). Debounced rather than search-on-Enter so it behaves
  // like the rest of the filter box, where a change reloads the list.
  companyName: { value: '', param: 'company_name' },
  client: { value: [], options: [], param: 'client', type: 'ids' },
  campaign: { value: [], options: [], param: 'campaign', type: 'ids' },
  emailTemplate: { value: [], options: [], param: 'email_template', type: 'ids' },
  nextAction: { value: [], param: 'crm_next_action' },
  nextActionDueAfter: { value: '', param: 'crm_next_action_due_after' },
  nextActionDueBefore: { value: '', param: 'crm_next_action_due_before' },
  crmDetail: { value: [], options: [], param: 'crm_detail', type: 'ids' },
  excludeHasReplies: { value: false, param: 'exclude_has_replies' },
  // The queue spans every mailbox the user can send from, so it needs narrowing
  // on the two axes it now mixes: which mailbox a draft goes out from (mailbox
  // ids, the same `mailbox` filter the Threads tab uses), and whose draft it is
  // -- `mineOnly` is the common case of the latter and resolves to
  // created_by=<me> at query time. An old URL holding an address instead of an
  // id is dropped by the 'ids' hydration rather than sent.
  fromEmail: { value: [], options: [], param: 'mailbox', type: 'ids' },
  mineOnly: { value: false, param: 'created_by' },
  // Threads tab only: a draft has no reply state.
  replyState: {
    value: [],
    options: Object.entries(REPLY_STATES).map(([value, state]) => ({ label: state.label, value })),
    param: 'reply_state',
  },
})

// --- Filters in the URL ----------------------------------------------------
// Opening a draft and coming back used to land on an unfiltered queue, because
// the only thing carried between the list and a draft was the tab. The filter
// state lives in the page's own query string instead, so the round trip restores
// it -- and a filtered queue can be linked to or reloaded.

// Only filters that hold a value appear, so a default queue has a clean URL.
// Arrays go as repeated params rather than a joined string: next-action values
// are free text with spaces and punctuation, and letting the router encode each
// one avoids inventing a separator they might contain.
function filtersAsQuery() {
  const query = {}
  Object.entries(filters).forEach(([key, filter]) => {
    const value = filter.value
    if (Array.isArray(value)) {
      if (value.length) query[key] = value.map(String)
    } else if (typeof value === 'boolean') {
      if (value) query[key] = '1'
    } else if (String(value || '').trim()) {
      query[key] = String(value).trim()
    }
  })
  return query
}

// replace, not push: adjusting a filter shouldn't put an entry in the history for
// the back button to walk through one keystroke at a time.
function syncFiltersToUrl() {
  const query = { ...filtersAsQuery(), tab: activeTab.value }
  if (threadBox.value !== 'inbox') query.box = threadBox.value
  router.replace({ query })
}

function hydrateFiltersFromUrl() {
  Object.entries(filters).forEach(([key, filter]) => {
    const raw = route.query[key]
    if (raw === undefined || raw === null) return
    if (Array.isArray(filter.value)) {
      // A single repeated param arrives as a bare string, several as an array.
      const items = [].concat(raw).filter((item) => item !== null && item !== '')
      filter.value = filter.type === 'ids'
        ? items.map(Number).filter((n) => !Number.isNaN(n))
        : items.map(String)
    } else if (typeof filter.value === 'boolean') {
      filter.value = raw === '1' || raw === 'true'
    } else {
      filter.value = String(raw)
    }
  })
}

// Read once, during setup: the child multi-selects take their initial selection
// from these values as they mount, so hydrating later would leave them briefly
// showing an empty selection and rely on a prop watcher to correct it.
hydrateFiltersFromUrl()

// Fixed option list for the next-action filter (same list the draft editor uses).
const nextActionOptions = CRM_NEXT_ACTION_OPTIONS

// Status -> display label. Drives the row pill (no longer a filter dropdown).
const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'ready', label: 'Ready' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'sent', label: 'Sent' },
  { value: 'failed', label: 'Failed' },
  { value: 'discarded', label: 'Discarded' },
]

// Whether a given filter currently holds a value, by its key in `filters`. One
// predicate over all three shapes the box uses -- array (multi-select), boolean
// (toggle), string (text / date) -- so the label indicators, the per-filter x and
// Clear all can't disagree about what "set" means.
function filterIsActive(...keys) {
  return keys.some((key) => {
    const value = filters[key] && filters[key].value
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'boolean') return value
    return !!String(value || '').trim()
  })
}

// How many values a multi-select holds; 0 for every other shape.
function filterCount(key) {
  const value = filters[key] && filters[key].value
  return Array.isArray(value) ? value.length : 0
}

// Empty one filter (or a group of them, like the due-date pair) back to its
// no-value state and reload. Resetting to the right empty per shape.
function clearFilter(...keys) {
  // A pending company-search debounce would otherwise fire a second reload just
  // after this one, with the value we've already cleared.
  if (companyNameTimer) { clearTimeout(companyNameTimer); companyNameTimer = null }
  keys.forEach((key) => {
    const value = filters[key].value
    if (Array.isArray(value)) filters[key].value = []
    else if (typeof value === 'boolean') filters[key].value = false
    else filters[key].value = ''
  })
  clearSelection()
  syncFiltersToUrl()
  loadList(1)
  loadSummary()
}

// Every filter, grouped as the box presents them -- the due-date pair is two
// values but one field, so it counts once. Listing a new filter here is all it
// takes to reach Clear all and the active count.
const FILTER_GROUPS = [
  ['companyName'], ['client'], ['campaign'], ['emailTemplate'], ['nextAction'],
  ['nextActionDueAfter', 'nextActionDueBefore'], ['crmDetail'], ['fromEmail'],
  ['excludeHasReplies'], ['mineOnly'], ['replyState'],
]
const FILTER_KEYS = FILTER_GROUPS.flat()

// Filters that only exist on the Threads tab. Elsewhere they're hidden and not
// sent, so they mustn't count as active there either -- "1 active" with nothing
// visible to clear would be a puzzle. Clear all still empties them.
const THREADS_ONLY_FILTERS = ['replyState']
const appliedFilterGroups = computed(() => FILTER_GROUPS.filter(
  (group) => activeTab.value === 'threads' || !group.some((key) => THREADS_ONLY_FILTERS.includes(key))))

const hasActiveFilters = computed(() => filterIsActive(...appliedFilterGroups.value.flat()))
const activeFilterCount = computed(
  () => appliedFilterGroups.value.filter((group) => filterIsActive(...group)).length)

const currentUserId = computed(() => store.state.auth.user && store.state.auth.user.id)

// Only worth showing the mailbox on each row (and the From filter) once the queue
// actually spans more than one -- for an analyst with a single mailbox it's a
// column of the same address repeated.
const hasMultipleFromAddresses = computed(() => filters.fromEmail.options.length > 1)

// Rows from a shared mailbox can belong to a colleague. Only those are marked --
// tagging every row with its owner would just repeat the reader's own name.
function isSomeoneElses(draft) {
  return !!(draft.owner && draft.owner.id !== currentUserId.value)
}

// First + last initial, so the marker stays a fixed-width badge no matter how
// long the name is; the full name is on the row's title attribute. The server
// falls back to the username when a user has no first/last name, which has no
// second word to take -- use its first two characters instead.
function ownerInitials(owner) {
  const parts = ((owner && owner.name) || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

const emptyMessage = computed(() => {
  if (activeTab.value === 'threads') {
    if (threadBox.value === 'inbox') return 'Nothing in your Inbox. A thread appears here once its first email sends, and comes back from the Archive when someone replies.'
    if (threadBox.value === 'archive') return 'Nothing archived.'
    if (threadBox.value === 'all') return 'No conversations yet. A thread appears here once its first email sends.'
    return 'No threads in this folder.'
  }
  if (activeTab.value === 'sent') return 'No sent drafts.'
  if (activeTab.value === 'failed') return 'No failed sends — nothing needs attention.'
  if (activeTab.value === 'scheduled') return 'No scheduled drafts. Open a draft and "Schedule send" to queue one for later.'
  if (activeTab.value === 'discarded') return 'No discarded drafts.'
  return 'No drafts. Select companies and a template, then "Prepare drafts" to queue outreach here.'
})

// 'ready' is not surfaced in the summary: nothing in the UI moves a draft into
// that status, so the chip was always 0. It stays in statusOptions so any draft
// still holding it renders as "Ready" in its row pill.
const SUMMARY_HIDDEN_STATUSES = ['ready']

// One chip per display status, in statusOptions order. 'blocked' folds into
// 'Draft' (matching the row pill), so its count is added there.
const summaryRows = computed(() => {
  const counts = summary.value?.counts || {}
  return statusOptions
    .filter(opt => !SUMMARY_HIDDEN_STATUSES.includes(opt.value))
    .map(opt => {
      let count = counts[opt.value] || 0
      if (opt.value === 'draft') count += counts.blocked || 0
      return { value: opt.value, label: opt.label, count }
    })
})

// The replies inbox reads a different endpoint and a different row shape, so it
// gets its own loader rather than another branch inside loadDrafts.
async function loadThreads(toPage = 1) {
  loading.value = true
  try {
    const params = { page: toPage }
    // Every filter applies here too. Template and CRM detail belong to a
    // message, so they match a thread with any message carrying them; the
    // rest are the thread's or its company's (see OutreachThreadFilter).
    if (filters.companyName.value.trim()) params.company_name = filters.companyName.value.trim()
    if (filters.client.value.length) params.client = filters.client.value
    if (filters.campaign.value.length) params.campaign = filters.campaign.value
    if (filters.emailTemplate.value.length) params.email_template = filters.emailTemplate.value
    if (filters.nextAction.value.length) params.crm_next_action = filters.nextAction.value
    if (filters.nextActionDueAfter.value) params.crm_next_action_due_after = filters.nextActionDueAfter.value
    if (filters.nextActionDueBefore.value) params.crm_next_action_due_before = filters.nextActionDueBefore.value
    if (filters.crmDetail.value.length) params.crm_detail = filters.crmDetail.value
    if (filters.excludeHasReplies.value) params.exclude_has_replies = true
    if (filters.fromEmail.value.length) params.mailbox = filters.fromEmail.value
    if (filters.mineOnly.value && currentUserId.value) params.created_by = currentUserId.value
    if (filters.replyState.value.length) params.reply_state = filters.replyState.value
    if (threadBox.value !== 'all') params.box = threadBox.value
    // Counts alongside, so they track whatever just moved.
    loadBoxCounts()
    const data = await getOutreachThreads(params, store.state.auth.token)
    threads.value = data.results || []
    page.value = data.page || toPage
    next.value = data.next
    prev.value = data.previous
    numPages.value = data.num_pages || 1
    syncPageToUrl(page.value)
  } catch (e) {
    if (isPastLastPage(e, toPage)) return await loadThreads(1)
    $toast.error('Failed to load threads')
  } finally {
    loading.value = false
  }
}

// --- Folders ------------------------------------------------------------------

async function loadFolders() {
  try {
    folders.value = await getOutreachFolders(store.state.auth.token) || []
  } catch (e) {
    $toast.error('Failed to load folders')
  }
}

async function loadBoxCounts() {
  try {
    boxCounts.value = await getOutreachThreadBoxCounts(store.state.auth.token)
  } catch (e) {
    // non-critical; the sidebar just goes without numbers
  }
}

function selectBox(box) {
  if (threadBox.value === box) return
  threadBox.value = box
  const query = { ...route.query, tab: activeTab.value }
  if (box === 'inbox') delete query.box
  else query.box = box
  delete query.page
  router.replace({ query })
  clearSelection()
  loadThreads(1)
}

// Everywhere a selection can go from here, the current box left out.
const moveDestinations = computed(() => {
  const all = [
    { value: 'inbox', label: 'Inbox' },
    ...folders.value.map((f) => ({ value: String(f.id), label: f.name })),
    { value: 'archive', label: 'Archive' },
  ]
  return all.filter((d) => d.value !== threadBox.value)
})

function onMoveSelect(event) {
  const to = event.target.value
  // Back to the placeholder, so the same destination can be picked again.
  event.target.value = ''
  if (to) onMoveThreads(selectedDraftIds.value, to)
}

function destinationLabel(to) {
  if (to === 'inbox') return 'Inbox'
  if (to === 'archive') return 'Archive'
  const folder = folders.value.find((f) => String(f.id) === String(to))
  return folder ? folder.name : 'folder'
}

async function onMoveThreads(threadIds, to) {
  if (!threadIds.length) return
  moving.value = true
  try {
    const res = await moveOutreachThreads(threadIds, to, store.state.auth.token)
    const moved = res.moved || 0
    $toast.success(`Moved ${moved} thread${moved === 1 ? '' : 's'} to ${destinationLabel(to)}`)
    const movedIds = new Set(threadIds)
    selectedDraftIds.value = selectedDraftIds.value.filter((id) => !movedIds.has(id))
    loadThreads(page.value)
  } catch (e) {
    $toast.error(e?.response?.data?.detail || 'Failed to move threads')
  } finally {
    moving.value = false
  }
}

// The bulk button unpins only when every selected thread on screen is pinned;
// otherwise it pins. Selection spans pages, so it judges by what it can see.
const selectionAllPinned = computed(() => {
  const selected = new Set(selectedDraftIds.value)
  const onScreen = threads.value.filter((t) => selected.has(t.id))
  return onScreen.length > 0 && onScreen.every((t) => t.pinned)
})

async function onPinThreads(threadIds, pinned) {
  if (!threadIds.length) return
  moving.value = true
  try {
    const res = await pinOutreachThreads(threadIds, pinned, store.state.auth.token)
    const n = (pinned ? res.pinned : res.unpinned) || 0
    $toast.success(`${pinned ? 'Pinned' : 'Unpinned'} ${n} thread${n === 1 ? '' : 's'}`)
    loadThreads(page.value)
  } catch (e) {
    $toast.error(e?.response?.data?.detail || `Failed to ${pinned ? 'pin' : 'unpin'} threads`)
  } finally {
    moving.value = false
  }
}

const REPLY_STATUS_LABELS = { no_reply_needed: 'No reply needed', do_not_contact: 'Do not contact' }

function replyStateLabel(state) {
  return (REPLY_STATES[state] || REPLY_STATES.awaiting_reply).label
}

function replyStateClass(state) {
  return (REPLY_STATES[state] || REPLY_STATES.awaiting_reply).cls
}

function replyStateTitle(thread) {
  if (thread.reply_state === 'not_delivered') return "The latest message bounced -- it wasn't delivered"
  if (thread.reply_status && thread.reply_state === thread.reply_status && thread.reply_status_set_by) {
    return `Marked by ${thread.reply_status_set_by} on ${formatDate(thread.reply_status_set_at)}`
  }
  return ''
}

// A bounce isn't anyone's to answer, so it's the one state with no menu.
function canRuleOnReply(thread) {
  return thread.reply_state !== 'not_delivered'
}

// What the label's menu offers: either ruling, minus the one already set, plus
// a way back from it. Clearing returns the thread to whatever its messages say
// -- Needs reply or Awaiting reply -- so the option names the ruling it undoes.
function replyStatusOptions(thread) {
  const state = thread.reply_state
  const options = []
  if (state === 'no_reply_needed') options.push({ value: null, label: 'Clear no reply needed' })
  if (state === 'do_not_contact') options.push({ value: null, label: 'Clear do not contact' })
  if (state !== 'no_reply_needed') options.push({ value: 'no_reply_needed', label: 'No reply needed' })
  if (state !== 'do_not_contact') options.push({ value: 'do_not_contact', label: 'Do not contact' })
  return options
}

const replyMenuThreadId = ref(null)

function toggleReplyMenu(threadId) {
  replyMenuThreadId.value = replyMenuThreadId.value === threadId ? null : threadId
}

function closeReplyMenu() {
  replyMenuThreadId.value = null
}

onMounted(() => document.addEventListener('click', closeReplyMenu))
onBeforeUnmount(() => document.removeEventListener('click', closeReplyMenu))

async function onSetReplyStatus(threadIds, replyStatus) {
  if (!threadIds.length) return
  closeReplyMenu()
  moving.value = true
  try {
    const res = await setOutreachThreadReplyStatus(threadIds, replyStatus, store.state.auth.token)
    const n = res.updated || 0
    const threadsText = `${n} thread${n === 1 ? '' : 's'}`
    $toast.success(replyStatus
      ? `Marked ${threadsText} as ${REPLY_STATUS_LABELS[replyStatus]}`
      : `Cleared the reply status on ${threadsText}`)
    loadThreads(page.value)
  } catch (e) {
    $toast.error(e?.response?.data?.detail || 'Failed to update reply status')
  } finally {
    moving.value = false
  }
}

async function onCreateFolder(name) {
  try {
    const folder = await createOutreachFolder(name, store.state.auth.token)
    folders.value = [...folders.value, folder]
    loadBoxCounts()
  } catch (e) {
    $toast.error(e?.response?.data?.name?.[0] || 'Failed to create folder')
  }
}

async function onRenameFolder({ folder, name }) {
  try {
    const updated = await updateOutreachFolder(folder.id, { name }, store.state.auth.token)
    folders.value = folders.value.map((f) => (f.id === updated.id ? updated : f))
    // Rows under All show folder names.
    if (threadBox.value === 'all') loadThreads(page.value)
  } catch (e) {
    $toast.error(e?.response?.data?.name?.[0] || 'Failed to rename folder')
  }
}

async function onDeleteFolder(folder) {
  const n = boxCounts.value?.folders?.[folder.id] || 0
  const what = n ? ` Its ${n} thread${n === 1 ? '' : 's'} will go back to your Inbox.` : ''
  if (!window.confirm(`Delete the folder "${folder.name}"?${what}`)) return
  try {
    await deleteOutreachFolder(folder.id, store.state.auth.token)
    folders.value = folders.value.filter((f) => f.id !== folder.id)
    if (threadBox.value === String(folder.id)) selectBox('inbox')
    else loadThreads(page.value)
  } catch (e) {
    $toast.error('Failed to delete folder')
  }
}

// Open the conversation to read and answer it, by the thread itself -- a thread
// imported from Outlook has no email of ours to open it through.
function openThread(thread) {
  const query = { ...route.query, tab: activeTab.value }
  router.push({ path: `/outreach-threads/${thread.id}`, query })
}

// Reload whichever list the active tab shows. Filter changes, paging and refresh
// go through this: calling loadDrafts directly from the Threads tab refetched a
// list that isn't on screen and left the threads as they were.
function loadList(toPage = 1) {
  if (activeTab.value === 'threads') return loadThreads(toPage)
  if (activeTab.value !== 'dashboard') return loadDrafts(toPage)
}

// The current page lives in the URL beside the tab and filters, so opening a row
// and coming Back returns to the page it was on rather than page 1. Page 1 is
// left out, keeping a default queue's URL clean.
function pageFromUrl() {
  const n = parseInt(route.query.page, 10)
  return Number.isInteger(n) && n > 1 ? n : 1
}

function syncPageToUrl(p) {
  const query = { ...route.query }
  if (p > 1) query.page = String(p)
  else delete query.page
  if (query.page !== route.query.page) router.replace({ query })
}

// A page from the URL can be past the end by the time it's reloaded -- rows
// sent or discarded meanwhile -- and the API answers that with a 404. Fall back
// to page 1 rather than showing an error for a list that exists.
function isPastLastPage(e, toPage) {
  return toPage > 1 && e?.response?.status === 404
}

async function loadDrafts(toPage = 1) {
  loading.value = true
  try {
    const token = store.state.auth.token
    const params = { page: toPage, status_group: activeTab.value }
    // Arrays are serialized as repeated params by the service's paramsSerializer.
    if (filters.companyName.value.trim()) params.company_name = filters.companyName.value.trim()
    if (filters.client.value.length) params.client = filters.client.value
    if (filters.campaign.value.length) params.campaign = filters.campaign.value
    if (filters.emailTemplate.value.length) params.email_template = filters.emailTemplate.value
    if (filters.nextAction.value.length) params.crm_next_action = filters.nextAction.value
    if (filters.nextActionDueAfter.value) params.crm_next_action_due_after = filters.nextActionDueAfter.value
    if (filters.nextActionDueBefore.value) params.crm_next_action_due_before = filters.nextActionDueBefore.value
    if (filters.crmDetail.value.length) params.crm_detail = filters.crmDetail.value
    if (filters.excludeHasReplies.value) params.exclude_has_replies = true
    if (filters.fromEmail.value.length) params.mailbox = filters.fromEmail.value
    if (filters.mineOnly.value && currentUserId.value) params.created_by = currentUserId.value
    const res = await getOutreachDrafts(token, params)
    drafts.value = res.results || []
    page.value = res.page || toPage
    next.value = res.next
    prev.value = res.prev
    numPages.value = res.numPages || 1
    syncPageToUrl(page.value)
  } catch (e) {
    if (isPastLastPage(e, toPage)) return await loadDrafts(1)
    $toast.error('Failed to load drafts')
  } finally {
    loading.value = false
  }
}

// Summary counts span every tab, so it only depends on the refine filters
// (client/campaign/email_template) -- never status_group.
async function loadSummary() {
  summaryLoading.value = true
  try {
    const token = store.state.auth.token
    const params = {}
    if (filters.companyName.value.trim()) params.company_name = filters.companyName.value.trim()
    if (filters.client.value.length) params.client = filters.client.value
    if (filters.campaign.value.length) params.campaign = filters.campaign.value
    if (filters.emailTemplate.value.length) params.email_template = filters.emailTemplate.value
    if (filters.nextAction.value.length) params.crm_next_action = filters.nextAction.value
    if (filters.nextActionDueAfter.value) params.crm_next_action_due_after = filters.nextActionDueAfter.value
    if (filters.nextActionDueBefore.value) params.crm_next_action_due_before = filters.nextActionDueBefore.value
    if (filters.crmDetail.value.length) params.crm_detail = filters.crmDetail.value
    if (filters.excludeHasReplies.value) params.exclude_has_replies = true
    if (filters.fromEmail.value.length) params.mailbox = filters.fromEmail.value
    if (filters.mineOnly.value && currentUserId.value) params.created_by = currentUserId.value
    summary.value = await getOutreachDraftStatusSummary(token, params)
  } catch (e) {
    // non-critical; leave the previous summary in place
  } finally {
    summaryLoading.value = false
  }
}

function onFilterChange(key, selectedIds) {
  filters[key].value = selectedIds
  clearSelection()
  syncFiltersToUrl()
  loadList(1)
  loadSummary()
}

function selectTab(key) {
  // Switching tabs doesn't change the summary (it spans all tabs) -- only reload the list.
  if (activeTab.value === key) return
  activeTab.value = key
  // A new tab starts on page 1, so its page number doesn't carry over.
  const query = { ...route.query, tab: key }
  delete query.page
  router.replace({ query })
  clearSelection()
  loadList(1)
}

function onDateFilterChange() {
  clearSelection()
  syncFiltersToUrl()
  loadList(1)
  loadSummary()
}

// Renamed-for-clarity alias of the above: any non-typeahead control that should
// reload the list immediately.
const onTextFilterChange = onDateFilterChange

// Typing reloads on a pause rather than per keystroke -- a company search is
// several characters long and each one would otherwise cost a list + summary
// round trip. Enter still fires it immediately via @keyup.enter.
let companyNameTimer = null
function onCompanyNameInput() {
  if (companyNameTimer) clearTimeout(companyNameTimer)
  companyNameTimer = setTimeout(onTextFilterChange, 400)
}

// Enter searches now -- dropping the pending timer so it doesn't fire a second
// identical reload a moment later.
function onCompanyNameEnter() {
  if (companyNameTimer) { clearTimeout(companyNameTimer); companyNameTimer = null }
  onTextFilterChange()
}

// Clear all is just every filter cleared at once -- same per-shape reset, same
// reload, no second list of filters to keep in step.
function clearFilters() {
  clearFilter(...FILTER_KEYS)
}

// Handle dashboard filter updates (client/campaign selection for dashboard)
function onDashboardUpdateFilter(payload) {
  const { key, value } = payload
  filters[key].value = value
}

async function loadFilterOptions() {
  const token = store.state.auth.token
  try {
    const campaigns = await getMinimalActiveCampaigns(token)
    filters.campaign.options = (campaigns || []).map(c => ({ label: c.campaign_name, value: c.id }))
  } catch (e) {
    $toast.error('Failed to load campaigns')
  }
  try {
    const res = await getClients({ token, endpoint: '?get_all=true' })
    filters.client.options = (res.data || []).map(c => ({ label: c.client_name, value: c.id }))
  } catch (e) {
    $toast.error('Failed to load clients')
  }
  try {
    const templates = await getEmailTemplates(token)
    filters.emailTemplate.options = (templates || []).map(t => ({ label: t.email_template_name, value: t.id }))
  } catch (e) {
    $toast.error('Failed to load email templates')
  }
  try {
    const res = await getActions(token)
    filters.crmDetail.options = (res.data || []).map(a => ({ label: a.action_name, value: a.id }))
  } catch (e) {
    $toast.error('Failed to load CRM details')
  }
  try {
    // Mailboxes represented in this user's queue, with how many drafts each holds.
    const addresses = await getOutreachDraftFromAddresses(token)
    filters.fromEmail.options = (addresses || []).map(a => ({
      label: `${a.email_address} (${a.count})`,
      value: a.id,
    }))
  } catch (e) {
    $toast.error('Failed to load from addresses')
  }
}

function openDraft(draft) {
  // Carry the whole query -- tab AND filters -- so the draft's Back button
  // returns to the queue as it was left, not to an unfiltered list.
  const query = { ...route.query, tab: activeTab.value }
  // Sent drafts can't be edited -- open the mail-client-style preview instead.
  if (draft.status === 'sent') {
    router.push({ path: `/outreach-drafts/${draft.id}/preview`, query })
  } else {
    router.push({ path: `/outreach-drafts/${draft.id}`, query })
  }
}

function statusLabel(status) {
  // A draft with no recipient (blocked) is just a Draft as far as the queue shows.
  const normalized = status === 'blocked' ? 'draft' : status
  const found = statusOptions.find(o => o.value === normalized)
  return found ? found.label : normalized
}

function statusClass(status) {
  switch (status) {
    case 'sent': return 'bg-green-100 text-green-800'
    case 'scheduled': return 'bg-indigo-100 text-indigo-800'
    case 'ready': return 'bg-blue-100 text-blue-800'
    case 'in_progress': return 'bg-yellow-100 text-yellow-800'
    case 'failed': return 'bg-red-100 text-red-800'
    case 'discarded': return 'bg-gray-100 text-gray-500'
    // 'draft' and 'blocked' both fall through to the neutral default.
    default: return 'bg-gray-100 text-gray-700'
  }
}

function formatDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  const p = (n) => String(n).padStart(2, '0')
  let h = d.getHours()
  const ampm = h >= 12 ? 'PM' : 'AM'
  h = h % 12 || 12
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${String(d.getFullYear()).slice(-2)} ${h}:${p(d.getMinutes())} ${ampm}`
}

// Due date is a date-only value ('YYYY-MM-DD'); format its parts directly to
// mm/dd/yy rather than via Date(), which would shift it by the local tz offset.
function formatDueDate(d) {
  if (!d) return '—'
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/)
  return m ? `${m[2]}/${m[3]}/${m[1].slice(-2)}` : d
}

// Statuses that mean the draft hasn't gone out yet (mirrors the backend
// status_group 'active'). Failed sends have their own tab + red alert, so they
// aren't folded into the age-based stale signal here. Sent/discarded are never stale.
const UNSENT_STATUSES = ['draft', 'in_progress', 'ready', 'blocked']
const STALE_DAYS = 7

function ageInDays(iso) {
  if (!iso) return 0
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
}

// An unsent draft sitting in the queue for over a week needs attention.
function isStale(draft) {
  if (!UNSENT_STATUSES.includes(draft.status)) return false
  return ageInDays(draft.created_at) > STALE_DAYS
}

// A scheduled draft whose send time passed 30+ min ago hasn't been picked up by
// the send sweep -- a sign the job is stuck/stopped. Mirrors the backend
// overdue_scheduled window.
const OVERDUE_SCHEDULED_MINUTES = 30
function isOverdueScheduled(draft) {
  if (draft.status !== 'scheduled' || !draft.scheduled_send_at) return false
  return (Date.now() - new Date(draft.scheduled_send_at).getTime()) > OVERDUE_SCHEDULED_MINUTES * 60000
}

function refresh() {
  loadList(page.value)

  loadSummary()
}

const canUseSupervisorMode = computed(() => isOutreachSupervisor(store))
// Everything on the page is scoped by it: the lists, counts and the From
// filter's options.
watch(outreachSupervisorMode, () => {
  loadFilterOptions()
  loadList(1)
  loadSummary()
})

onBeforeUnmount(() => {
  if (companyNameTimer) clearTimeout(companyNameTimer)
})

onMounted(() => {
  loadFilterOptions()
  loadFolders()
  loadList(pageFromUrl())
  loadSummary()
})
</script>

<style scoped>
/* Was a flat max-w-6xl (72rem), which left a wide unused gutter either side on a
   big monitor -- expensive on a page this column-dense. Still centered, but with
   half that gutter given back to the content: the space either side is now
   (100vw - 72rem)/4 rather than (100vw - 72rem)/2. Below 72rem it just fills the
   available width, exactly as before. */
.page-card {
  max-width: calc((100vw + 72rem) / 2);
}

/* Shrink the selected pills so they fit within the input row (the shared
   component's default pill overflows the ~32px tall box and gets clipped). */
.filter-typeahead :deep(.bg-indigo-200) {
  font-size: 0.7rem;
  line-height: 1rem;
  padding-top: 1px;
  padding-bottom: 1px;
  margin-top: 2px;
  margin-bottom: 2px;
}

/* The Sent tab is column-dense; trim horizontal cell padding so it fits the card.
   Overrides the cells' pr-4/pr-3 utilities (scoped selector outranks them). */
.is-compact th,
.is-compact td {
  padding-right: 0.5rem;
}
</style>
